/**
 * Explainability Service — wraps /api/admin/ai/explain/
 * Phase 4.8-B — WQ-198
 */

// === Types ===

export interface ExplainRequest {
  prediction_id: string;
  method?: string;
  num_features?: number;
}

export interface Explanation {
  id: string;
  prediction_id: string;
  method: string;
  feature_importance: FeatureContribution[];
  base_value: number | null;
  output_value: number | null;
  created_at: string;
}

export interface FeatureContribution {
  feature: string;
  value: unknown;
  contribution: number;
  abs_contribution: number;
}

export interface BatchExplainRequest {
  prediction_ids: string[];
  method?: string;
  num_features?: number;
}

export interface BatchExplanation {
  batch_id: string;
  explanations: Explanation[];
  aggregate_importance: FeatureContribution[];
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function computeExplanation(
  authFetch: FetchFn,
  request: ExplainRequest
): Promise<Explanation> {
  const res = await authFetch('/api/admin/ai/explain/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to compute explanation: ${res.status}`);
  return res.json();
}

export async function getExplanation(
  authFetch: FetchFn,
  explanationId: string
): Promise<Explanation> {
  const res = await authFetch(`/api/admin/ai/explain/${explanationId}`);
  if (!res.ok) throw new Error(`Failed to get explanation: ${res.status}`);
  return res.json();
}

export async function computeBatchExplanation(
  authFetch: FetchFn,
  request: BatchExplainRequest
): Promise<BatchExplanation> {
  const res = await authFetch('/api/admin/ai/explain/batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to compute batch explanation: ${res.status}`);
  return res.json();
}

export async function getBatchExplanation(
  authFetch: FetchFn,
  batchId: string
): Promise<BatchExplanation> {
  const res = await authFetch(`/api/admin/ai/explain/batch/${batchId}`);
  if (!res.ok) throw new Error(`Failed to get batch explanation: ${res.status}`);
  return res.json();
}

export async function exportExplanation(
  authFetch: FetchFn,
  explanationId: string,
  format: 'json' | 'csv' = 'json'
): Promise<Blob> {
  const res = await authFetch(`/api/admin/ai/explain/${explanationId}/export?format=${format}`);
  if (!res.ok) throw new Error(`Failed to export explanation: ${res.status}`);
  return res.blob();
}

// === WQ-240 helpers (Phase 4.10 β5 — ExplainabilityLauncher) ===

/**
 * Real backend response shape for POST /api/admin/ai/explain/prediction/{id}.
 * Matches platform-api/app/routers/explainability_router.py ExplanationResponse.
 */
export interface ExplanationRunResponse {
  method: string;
  base_value: number;
  feature_importances: Array<Record<string, unknown>>;
  prediction_value: number | null;
  computed_at: string | null;
}

export interface BatchExplanationRunResponse {
  batch_id: string;
  sample_size: number;
  method: string;
  mean_feature_importances: Array<Record<string, unknown>>;
  computed_at: string | null;
}

/**
 * Compute SHAP explanation for a single prediction (WQ-240).
 * Wraps POST /api/admin/ai/explain/prediction/{prediction_id}.
 */
export async function runPredictionExplanation(
  authFetch: FetchFn,
  predictionId: string,
  body: { max_features?: number }
): Promise<ExplanationRunResponse> {
  const res = await authFetch(`/api/admin/ai/explain/prediction/${predictionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to compute prediction explanation: ${res.status}`);
  return res.json();
}

/**
 * Compute aggregate SHAP feature importance for a batch (WQ-240).
 * Wraps POST /api/admin/ai/explain/batch/{batch_id}.
 */
export async function runBatchExplanation(
  authFetch: FetchFn,
  batchId: string,
  body: { sample_size?: number; max_features?: number }
): Promise<BatchExplanationRunResponse> {
  const res = await authFetch(`/api/admin/ai/explain/batch/${batchId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to compute batch explanation: ${res.status}`);
  return res.json();
}

/**
 * List recent predictions suitable for the launcher's Step 2 picker
 * (single-prediction mode). Reuses /api/admin/ai/predictions endpoint.
 */
export async function listExplainablePredictions(
  authFetch: FetchFn
): Promise<Array<{ id: string; model_id: string; created_at: string; batch_id: string | null }>> {
  const res = await authFetch('/api/admin/ai/predictions/?limit=50');
  if (!res.ok) throw new Error(`Failed to list predictions: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}
